// Third-party imports
import { MailOutline, Phone } from '@mui/icons-material';
import { Box, Button, Card, Stack, styled, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
// Custom hooks and selectors
import { useAppSelector } from '@hooks';

// Types and constants
import { Dealer } from '@api/types';
import { TextCodes } from '@api/types/translation/TextCodes';
import { GOOGLE_MAP_PROPS, SmartLink, Text, Title, useFindDealerContext } from '@components';
import { getSpecialPage } from '@store/app/app.slice';

// Utility functions
import { breakpoints } from '@themes/theme.helpers';
import { t } from '@translations';
import { sortByProximity } from '@utils/location.utils';

const DealerListGrid = styled(Box)`
  ${breakpoints.up('lg')} {
    display: block;
  }
  ${breakpoints.down('lg')} {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 12px;
  }
  ${breakpoints.down('sm')} {
    display: block;
  }
`;

const DealerCardWrapper = styled(Card)`
  background-color: ${({ theme }) => theme.palette.background.paper};
  margin-bottom: 16px;
  display: flex;
  flex-direction: column;
  padding: 16px;
  ${breakpoints.down('md')} {
    h4 {
      font-size: 18px;
    }
    p {
      font-size: 14px;
    }
  }
  ${breakpoints.down('sm')} {
    padding: 8px;
    h4 {
      font-size: 16px;
    }
  }
`;

export const FindDealerList = () => {
  const { currentDealerList, baseDealerList, map } = useFindDealerContext();
  const [centerLocation, setCenterLocation] = useState({
    lat: GOOGLE_MAP_PROPS.center.lat,
    lng: GOOGLE_MAP_PROPS.center.lng,
  });
  const hasDealers = currentDealerList && currentDealerList.length > 0;

  const sortedDealersByLocations = sortByProximity(
    {
      latitude: centerLocation.lat,
      longitude: centerLocation.lng,
    },
    currentDealerList || []
  );

  useEffect(() => {
    if (map) {
      const handleMapMove = () => {
        const center = map.getCenter();
        if (center) {
          setCenterLocation({ lat: center.lat(), lng: center.lng() });
        }
      };
      const listener = map.addListener('center_changed', handleMapMove);
      return () => {
        google.maps.event.removeListener(listener);
      };
    }
  }, [map]);

  return (
    <>
      {hasDealers && (
        <>
          <Text
            sx={{
              fontSize: { md: '32px !important', xs: '24px !important' },
              my: 2,
              color: '#667080',
            }}
          >
            {t(TextCodes.Dealer.Find.AllDealers)} ({currentDealerList?.length})
          </Text>
          <DealerListGrid sx={{ overflowY: { lg: 'scroll' }, height: { lg: '764px' } }}>
            {sortedDealersByLocations?.map((dealer) => (
              <FindDealerCard dealer={dealer} key={dealer.id} />
            ))}
          </DealerListGrid>
        </>
      )}
      {baseDealerList && !hasDealers ? (
        <Text
          sx={{
            fontSize: { md: '32px !important', xs: '24px !important' },
            my: 2,
            color: '#667080',
          }}
        >
          {t(TextCodes.Dealer.Find.NoDealersFound)}
        </Text>
      ) : null}
    </>
  );
};

const FindDealerCard = ({ dealer }: { dealer: Dealer }) => {
  const publicAddress = dealer?.address?.find((address) => address.addressType.includes('Public'));
  const dealerDetailsBaseUrl = useAppSelector(getSpecialPage('dealerDetailsPage'));
  const dealerDetailsHref = `${dealerDetailsBaseUrl}?id=${dealer?.customerNo}`;

  return (
    <DealerCardWrapper>
      <Title
        variant='h4'
        regular
        noDot
        className='line-clamp-2'
        sx={{ mb: 2, width: '100%', flexGrow: 1 }}
      >
        {publicAddress?.name}
      </Title>

      <Stack sx={{ width: '100%', flexBasis: 'max-content', flexGrow: 1 }}>
        <Typography
          variant='body2'
          color='text.secondary'
          sx={{
            textAlign: 'left !important',
          }}
        >
          <b>{t(TextCodes.Dealer.Find.City)}:</b>{' '}
          <span className='line-clamp-2'>{publicAddress?.city}</span>
        </Typography>
        <Typography
          variant='body2'
          color='text.secondary'
          sx={{
            textAlign: 'left !important',
          }}
        >
          <b>{t(TextCodes.Dealer.Find.Address)}:</b>{' '}
          <span className='line-clamp-2'>{publicAddress?.line1}</span>
        </Typography>
        {publicAddress?.workingHours && (
          <Typography
            variant='body2'
            color='text.secondary'
            sx={{
              textAlign: 'left !important',
            }}
          >
            <b>{t(TextCodes.Dealer.Find.WorkingHours)}:</b> {publicAddress?.workingHours}
          </Typography>
        )}
      </Stack>

      <Box
        sx={{
          '&:not(:empty)': { mt: 2 },
        }}
      >
        <ContactDetails IconComponent={Phone} text={publicAddress?.generalPhoneNumber} />
        <ContactDetails IconComponent={Phone} text={publicAddress?.salePhoneNumber} />
        <ContactDetails IconComponent={MailOutline} text={publicAddress?.contactEmail} />
        <ContactDetails IconComponent={MailOutline} text={publicAddress?.invoiceEmail} />
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
        <Button
          size='small'
          target='_blank'
          href={`https://www.google.com/maps/search/?api=1&query=${dealer?.latitude},${dealer?.longitude}`}
        >
          {t(TextCodes.Dealer.Find.ShowOnMap)}
        </Button>
        <Button size='small' href={dealerDetailsHref} LinkComponent={SmartLink}>
          {t(TextCodes.Dealer.Find.DealerDetails)}
        </Button>
      </Box>
    </DealerCardWrapper>
  );
};

const ContactDetails = ({
  IconComponent,
  text,
}: {
  IconComponent: React.ElementType;
  text?: string | null;
}) => {
  return text ? (
    <Box display='flex' alignItems='center'>
      <IconComponent sx={{ mr: 1 }} />
      <Typography variant='body2' mr={2}>
        {text}
      </Typography>
    </Box>
  ) : null;
};
